import { useCallback } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useHistory, useLocation } from "react-router";

import useCurrentLibraryChannel from "~/lib/hooks/useCurrentLibraryChannel";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { PARAM_NAME as COMPANY_PARAM_NAME } from "~/recordings/Filters/CompanyFilter";

import type { useFilterRecordingsByCompanyIntegrationsQuery } from "~/__generated__/useFilterRecordingsByCompanyIntegrationsQuery.graphql";

/**
 * @param domain company domain to filter by
 * @param basePath path to navigate to, will default to the current path if not specified
 * @returns A function that will navigate to the path with the company filter applied. Analytics included.
 */
export function useFilterRecordingsByCompany(domain: string, basePath?: string) {
	const history = useHistory();
	const location = useLocation();

	const currentLibraryChannel = useCurrentLibraryChannel();

	const { viewer } = useLazyLoadQuery<useFilterRecordingsByCompanyIntegrationsQuery>(
		graphql`
			query useFilterRecordingsByCompanyIntegrationsQuery {
				viewer {
					account {
						integrations {
							salesforce {
								isConnected
							}
							hubspot {
								accountConnected
							}
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-or-network" },
	);
	const { salesforce, hubspot } = viewer.account.integrations;

	const trackFilterSelection = useCallback(() => {
		let connectedCrm = "none";
		if (salesforce?.isConnected) connectedCrm = "salesforce";
		if (hubspot?.accountConnected) connectedCrm = "hubspot";
		dispatchAnalyticsTrack("Company Filter Applied", { crm: connectedCrm, location: currentLibraryChannel.name });
	}, [salesforce, hubspot, currentLibraryChannel.name]);

	const onClick = useCallback(() => {
		if (!domain) return;
		const search = new URLSearchParams(location.search);
		search.set(COMPANY_PARAM_NAME, domain);
		trackFilterSelection();
		history.push({
			pathname: basePath || location.pathname,
			search: search.toString(),
		});
	}, [domain, location.search, location.pathname, trackFilterSelection, history, basePath]);

	return onClick;
}
